import { useEffect, useState } from 'react';
import { purchasingAPI } from '../../lib/api';
import type { GoodsReceiptNote } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import { GRN_SEARCH_MIN_CHARS } from '../../components/quality/ncr-create/NCRGrnLinkDialog';

const GRN_SEARCH_LIMIT = 10;

/**
 * CR-8717: GRN search for the "Link a Goods Receipt Note" pop-up.
 *
 * Matches part of the GRN number (`grn_number__icontains`) in the view the
 * user is in — the same scope `useGrnLookup` links from, so every result can
 * be used. Picking one still goes through the exact-number lookup, which is
 * what fills the form.
 *
 * @example
 * const { results, searching } = useGrnSearch(grnInput, showGrnDialog);
 *
 * @param query - what the user has typed
 * @param enabled - false while the pop-up is closed, so nothing is fetched
 * @returns up to 10 matching GRNs, newest first, and whether a search is running
 */
export function useGrnSearch(query: string, enabled: boolean): {
  results: GoodsReceiptNote[];
  searching: boolean;
} {
  const { getViewQueryParams } = useView();
  const [results, setResults] = useState<GoodsReceiptNote[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = query.trim();
    if (!enabled || term.length < GRN_SEARCH_MIN_CHARS) {
      setResults([]);
      setSearching(false);
      return;
    }
    let live = true;
    setSearching(true);
    const t = setTimeout(() => {
      purchasingAPI.getGRNs({
        grn_number__icontains: term,
        page_size: GRN_SEARCH_LIMIT,
        all_suppliers: true,
        ...getViewQueryParams(),
      })
        .then((res) => { if (live) setResults(res.data.results ?? []); })
        .catch(() => { if (live) setResults([]); })
        .finally(() => { if (live) setSearching(false); });
    }, 300);
    return () => {
      // A slower answer for an earlier term must not replace the current one.
      live = false;
      clearTimeout(t);
    };
  }, [query, enabled, getViewQueryParams]);

  return { results, searching };
}
